<script setup lang="ts">
import type { HeaderProps } from '#layers/uk/types'

const props = defineProps<HeaderProps>()

const links = useLinks()

const { isMobile } = useDevice()
const { defaultBundle } = useSignupBundles()

function getBundleField(field: 'title' | 'subtitle'): string {
    const bundle = defaultBundle.value

    if (!bundle) return ''
    if ('promotionFields' in bundle) return bundle.promotionFields?.[field] ?? ''

    return bundle[field]
}

const title = computed(() => getBundleField('title'))
const subtitle = computed(() => getBundleField('subtitle'))

useHead({
    meta: [
        {
            name: 'theme-color',
            content: '#182138'
        }
    ]
})

const usps = props.content.usps ?? [
    {
        icon: '/media/landing-page/icons/lightning.png',
        title: 'Super Fast',
        subtitle: 'Withdrawals'
    },
    {
        icon: '/media/landing-page/icons/gift.png',
        title: 'Fun Bonuses',
        subtitle: '& Rewards'
    },
]
</script>

<template>
    <header
        class="section header"
        :class="{ 'header--trustpilot': content.trustpilotEnabled }"
    >
        <div class="header__video">
            <div class="header__video--ratio" />

            <UIIframe
                video-id="8ce85bb2bf529cb9962971470b49ded8"
                :fit-parent="true"
                loading="eager"
                :alt-text="content?.headline"
            />

            <div class="header__gradient" />
        </div>

        <div class="page-content-wrapper">
            <div class="heading-wrapper">
                <!-- eslint-disable vue/no-v-html The content of content.headline is safe -->
                <h1 class="header__title" v-html="title" />
                <!-- eslint-enable vue/no-v-html -->

                <div class="header__button-container">
                    <UIButton
                        v-bind="links.signup.props()"
                        class="header__button"
                        as="cta"
                        size="xl"
                        block
                        data-test="header-button-signup"
                    >
                        Continue

                        <template #append>
                            <UIIcon name="arrow-right" size="9px" />
                        </template>
                    </UIButton>

                    <!-- eslint-disable vue/no-v-html The content of content.headline is safe -->
                    <p class="header__description" v-html="subtitle" />
                    <!-- eslint-enable vue/no-v-html -->

                    <div class="terms">
                        <div class="terms__notice">
                            T&Cs apply - see below for details
                        </div>

                        <div class="usps">
                            <div v-for="usp in usps" :key="usp.icon" class="usp">
                                <div class="usp__icon">
                                    <NuxtImg
                                        :src="usp.icon"
                                        provider="cloudflare"
                                        preset="local"
                                        :width="isMobile ? '14px' : '18px'"
                                        :height="isMobile ? '14px' : '18px'"
                                    />
                                </div>
                                <div class="usp__content">
                                    <h4 class="usp__title">
                                        {{ usp.title }}
                                    </h4>
                                    <p class="usp__subtitle">
                                        {{ usp.subtitle }}
                                    </p>
                                </div>
                            </div>
                            <div class="usp--trustpilot">
                                <TrustpilotWidget />
                            </div>
                        </div>

                        <LandingPagePaymentMethods />
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>

<style scoped lang="scss">
$body-bg-color: v-bind(backgroundColor);
$yellow: #f6d701;
$media-height: 300px;
$media-height--sm: 40vh;
$media-height--tablet: 500px;
$media-height--xl: 550px;

.page-content-wrapper {
    display: grid;
    grid-template-rows: 1fr;
    grid-template-areas:
        'media'
        'content';
    align-items: center;
    justify-items: center;
    gap: 10px;
    position: relative;
    height: 100%;
    max-width: 1024px;
    margin: 0 auto;
    z-index: 4;
}

.heading-wrapper {
    display: grid;
    justify-items: center;
    gap: 6px;
}

.header {
    position: relative;
    color: $white;
    text-align: center;
    margin-bottom: 7vh; // Set to push down terms
    overflow: hidden;
    background: $body-bg-color;

    @include tablet {
        margin-bottom: unset;
    }

    a {
        color: $white;
    }

    &__video {
        position: relative;
        width: 100%;
        max-width: 860px;
        max-height: 52vh;
        overflow: hidden;
        /* Apply the dynamic color and a semi-transparent overlay */
        background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), $body-bg-color;

        @include tablet {
            margin: 0 auto;
        }

        @include desktop {
            max-height: 60vh;
        }

        @media (min-width: $screen-lg) and (min-height: 800px) {
            max-height: 478px;
        }

        :deep(.ui-iframe) {
            top: 40px;
        }

        video {
            position: absolute;
            top: 40px;
            left: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
            object-fit: cover;
        }

        &--ratio {
            padding-top: 100%;
            height: 0;
            z-index: 0;
        }
    }

    &__featured-image {
        position: absolute;
        bottom: -54px;
        right: -16%;

        @include mobile(sm) {
            bottom: -25px;
            right: -12%;
        }

        @include tablet {
            right: 4%;
            bottom: 10px;
        }

        @include desktop {
            right: 6%;
            bottom: 30px;
        }
    }

    &__gradient {
        position: absolute;
        bottom: -50px;
        height: 40%;
        width: 100%;
        background: linear-gradient(to bottom, transparent 0%, $body-bg-color 50%, $body-bg-color 100%);
        pointer-events: none;

        @include tablet {
            height: 120vh;
            width: 120%;
            min-height: 780px;
            top: 50%;
            bottom: auto;
            left: 50%;
            transform: translate(-50%, -68%);
            background: radial-gradient(transparent 0%, transparent 45%, $body-bg-color 58%, $body-bg-color 100%);
        }

        @media (min-width: $screen-lg) and (min-height: 940px) {
            height: 940px;
        }
    }

    &__usps {
        list-style: none;
        padding-left: unset;
        margin-bottom: 15px;
        font-size: 18px;

        p {
            margin-bottom: 0;
        }

        :deep(.usp.usp--underline) {
            text-decoration: underline;
        }

        :deep(em) {
            font-style: normal;
        }

        @include desktop {
            margin-bottom: 20px;
            font-size: 20px;
        }
    }

    &__title {
        font-weight: 800;
        font-family: $heading-font-family;
        text-transform: uppercase;
        line-height: 0.96;
        margin: 0;
        margin-top: -32px;
        width: 110%;
        transform: rotate(-3deg) translate3d(-1%, 0, 0);
        font-style: italic;
        /* Responsive font size for mobile (30px -> 40px) */
        font-size: clamp(30px, 11vw, 42px);

        :deep(p) {
            margin: 0 0 3px;
        }

        @include desktop {
            font-size: 70px;
            margin-top: -100px;
        }
    }

    &__description {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 12px;
        font-weight: 700;
        width: 100%;
        max-width: 100%;
        max-height: 45px;
        margin-bottom: 0;
        padding-top: 16px;

        @include mobile(sm) {
            min-width: 330px;
        }

        @include desktop {
            font-size: 14px;
        }

        @include desktop-xl {
            font-size: 16px;
        }
    }

    &__button-container {
        width: 90vw;
        display: flex;
        flex-direction: column;
        grid-template-columns: 1fr;
        align-items: center;
        margin-top: 20px;

        @include tablet {
            width: 50vw;
            max-width: 450px;
        }
    }

    &__button {
        max-width: 330px;
        text-transform: uppercase;
        font-size: 18px;

        animation: buttonWiggle 3000ms ease-in-out infinite;
    }
}

.terms {
    margin-top: 16px;

    &__notice {
        font-size: 10px;

        @include mobile(sm) {
            font-size: 12px;
        }

        :deep(.offer-terms__text a) {
            color: $white;
        }
    }
}

.usps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    align-items: center;
    justify-items: center;
    padding-top: 16px;
    gap: 14px;

    @include mobile {
        gap: 18px;
    }

    @include tablet {
        gap: 22px;
        padding-top: 32px;
    }

    .usp {
        position: relative;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 8px;

        @include mobile(sm) {
            white-space: nowrap;
        }

        &__icon {
            display: grid;
            align-items: start;
            align-content: center;
            justify-items: end;
            padding-top: 4px;
        }

        &__content {
            transform: rotate(-3deg);
            text-align: start;
        }

        &__title {
            color: #ffff00;
            font-size: 12px;
            font-style: italic;
            margin-block-start: unset;
            margin-block-end: unset;

            @include tablet {
                font-size: 16px;
            }
        }

        &__subtitle {
            font-size: 12px;
            font-style: italic;
            margin-block-start: unset;
            margin-block-end: unset;
            font-weight: bold;
            white-space: nowrap;
        }

        &--trustpilot {
            position: relative;
            display: inline-flex;
            grid-template-columns: unset;
        }

        &:after {
            /* ADDED */
            content: '';
            position: absolute;
            height: 28px; /* adjust this */
            top: 50%;
            right: -8px;
            border-left: 1px solid rgba(255, 255, 255, 0.1);
            transform: translateY(-50%);

            @include mobile {
                right: -12px;
                border-left-width: 2px;
            }

            @include desktop {
                right: -16px;
            }
        }

        &:last-child:after {
            /* ADDED */
            display: none; /* Hide the divider for the last block */
        }
    }
}

:deep(.text--featured) {
    color: #ffff00;
    font-size: 146%;
    white-space: nowrap;
    display: block;
}

@keyframes buttonWiggle {
    0%,
    5% {
        transform: rotate(0);
    }
    7% {
        transform: rotate(-3deg);
    }
    10% {
        transform: rotate(3deg);
    }
    12.5% {
        transform: rotate(-2deg);
    }
    15% {
        transform: rotate(2deg);
    }
    17.5% {
        transform: rotate(-1deg);
    }
    20% {
        transform: rotate(1deg);
    }
    22.5%,
    100% {
        transform: rotate(0);
    }
}
</style>
